import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView, useMotionValue, useSpring, useTransform } from 'framer-motion';
import {
  BookOpenIcon,
  ClockIcon,
  SparklesIcon,
  ChatBubbleBottomCenterTextIcon,
  BoltIcon,
  LinkIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { Logo } from '../common/Logo';
import { useIsMobile } from '../../hooks/useIsMobile';

// Book cover imports
import biologyCover from './book_covers/biology.png';
import chemistryCover from './book_covers/chemistry.png';
import physicsCover from './book_covers/physics.png';
import psychologyCover from './book_covers/psychology.png';
import anatomyCover from './book_covers/anatomy&physio.png';
import economicsCover from './book_covers/economics.png';

// OpenStax books for the preview card
const books = [
  { name: 'Biology 2e', cover: biologyCover },
  { name: 'Chemistry 2e', cover: chemistryCover },
  { name: 'College Physics 2e', cover: physicsCover },
  { name: 'Psychology 2e', cover: psychologyCover },
  { name: 'Anatomy & Physiology 2e', cover: anatomyCover },
  { name: 'Principles of Economics 3e', cover: economicsCover },
];

// 3D Tilt Card wrapper component
const TiltCard: React.FC<{
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  disableTilt?: boolean;
}> = ({ children, className, style, disableTilt }) => {
  const ref = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const mouseXSpring = useSpring(x, { stiffness: 300, damping: 30 });
  const mouseYSpring = useSpring(y, { stiffness: 300, damping: 30 });

  const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ['7deg', '-7deg']);
  const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ['-7deg', '7deg']);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current || disableTilt) return;
    const rect = ref.current.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const xPct = mouseX / width - 0.5;
    const yPct = mouseY / height - 0.5;
    x.set(xPct);
    y.set(yPct);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  // On mobile/touch devices, render without tilt effect
  if (disableTilt) {
    return (
      <div className={className} style={style}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        rotateX,
        rotateY,
        transformStyle: 'preserve-3d',
        ...style,
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Typing animation for demo card
const TypingDemo: React.FC<{ inView: boolean }> = ({ inView }) => {
  const [text, setText] = useState('');
  const [showResponse, setShowResponse] = useState(false);
  const question = "Explain ATP synthesis";

  useEffect(() => {
    if (!inView) return;

    let index = 0;
    setText('');
    setShowResponse(false);

    const typeQuestion = setInterval(() => {
      if (index < question.length) {
        setText(question.slice(0, index + 1));
        index++;
      } else {
        clearInterval(typeQuestion);
        setTimeout(() => setShowResponse(true), 500);
      }
    }, 50);

    return () => clearInterval(typeQuestion);
  }, [inView]);

  return (
    <div className="space-y-2">
      {/* Question bubble */}
      <div className="flex justify-end">
        <motion.div
          className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-xl rounded-tr-sm px-3 py-1.5 text-xs text-gray-700"
          whileHover={{ scale: 1.02 }}
        >
          {text}
          {text.length < question.length && (
            <motion.span
              animate={{ opacity: [0, 1, 0] }}
              transition={{ duration: 0.5, repeat: Infinity }}
              className="inline-block w-1 h-3 bg-cyan-400 ml-0.5"
            />
          )}
        </motion.div>
      </div>

      {/* Rich response */}
      {showResponse && (
        <motion.div
          initial={{ opacity: 0, y: 5, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          className="space-y-2"
        >
          {/* Main response */}
          <div className="rounded-lg px-2.5 py-2 text-[11px] bg-gray-50/80 border border-gray-200/60 text-gray-600">
            <span className="flex items-start gap-1.5">
              <SparklesIcon className="w-3 h-3 text-cyan-500 mt-0.5 flex-shrink-0" />
              <span>
                ATP is produced through{' '}
                <span className="text-cyan-600 font-medium">oxidative phosphorylation</span>
                {' '}in the mitochondria...
              </span>
            </span>
          </div>

          {/* Connections section */}
          <div className="rounded-lg px-2.5 py-2 bg-white/60 border border-gray-200/50">
            <div className="flex items-center gap-1 mb-1">
              <LinkIcon className="w-3 h-3 text-cyan-500" />
              <span className="text-[10px] font-semibold text-gray-700">Connections</span>
            </div>
            <p className="text-[10px] text-cyan-600 leading-relaxed">
              Remember the electron transport chain?
            </p>
            <span className="inline-block mt-1 text-[9px] px-1.5 py-0.5 bg-gray-100 rounded text-gray-500 font-medium">
              Biology 2e (Page 142)
            </span>
          </div>

          {/* Your Notes section */}
          <div className="rounded-lg px-2.5 py-2 bg-amber-50/50 border border-amber-200/50">
            <div className="flex items-center gap-1 mb-1">
              <DocumentTextIcon className="w-3 h-3 text-amber-500" />
              <span className="text-[10px] font-semibold text-gray-700">Your Notes</span>
            </div>
            <p className="text-[10px] text-gray-600 leading-relaxed">
              <span className="font-medium text-amber-700">You highlighted:</span> "ATP synthase acts like a turbine..."
            </p>
          </div>
        </motion.div>
      )}
    </div>
  );
};

const Features: React.FC = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: '-100px' });
  const [hoveredBook, setHoveredBook] = useState<number | null>(null);
  const { isMobile } = useIsMobile();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1] as const,
      },
    },
  };

  return (
    <section id="features" className="relative py-16 px-6 overflow-hidden">
      {/* Subtle ambient background */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-1/4 right-0 w-[500px] h-[500px] rounded-full opacity-10"
          style={{
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.4) 0%, transparent 60%)',
            filter: 'blur(80px)',
          }}
        />
        <div
          className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10"
          style={{
            background: 'radial-gradient(circle, rgba(6, 182, 212, 0.4) 0%, transparent 60%)',
            filter: 'blur(80px)',
          }}
        />
      </div>

      <div className="max-w-6xl mx-auto" ref={containerRef}>
        {/* Section header with animated underline */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] as const }}
          className="text-center mb-10"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/95 border border-gray-300 text-gray-800 text-sm font-semibold mb-6 shadow-md backdrop-blur"
          >
            <BoltIcon className="w-4 h-4 text-cyan-600" />
            In continuity
          </motion.span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-gray-900">
            Built for{' '}
            <span className="relative inline-block">
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
                }}
              >
                real studying
              </span>
              <motion.span
                className="absolute -bottom-2 left-0 right-0 h-1 rounded-full"
                style={{
                  background: 'linear-gradient(90deg, #06B6D4, #3B82F6)',
                }}
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.5 }}
              />
            </span>
          </h2>
          <p className="text-lg max-w-2xl mx-auto text-gray-600">
            Less chaos. More clarity. Faster recall.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="space-y-4"
        >
          {/* Row 1: 3 cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Card 1: Memory tagline */}
            <motion.div variants={itemVariants}>
              <TiltCard
                disableTilt={isMobile}
                className={`group relative overflow-hidden rounded-2xl border p-6 h-full cursor-pointer border-gray-400/70`}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(20px)',
                }}
              >
                {/* Hover glow */}
                <motion.div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background: 'radial-gradient(circle at 30% 30%, rgba(6, 182, 212, 0.15) 0%, transparent 60%)',
                  }}
                />
                {/* Border glow on hover */}
                <motion.div
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    boxShadow: 'inset 0 0 0 1px rgba(6, 182, 212, 0.3)',
                  }}
                />
                <div className="relative z-10" style={{ transform: 'translateZ(30px)' }}>
                  <motion.div
                    whileHover={{ rotate: [0, -10, 10, -5, 0], scale: 1.1 }}
                    transition={{ duration: 0.6 }}
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                    style={{
                      background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
                      boxShadow: '0 8px 30px rgba(6, 182, 212, 0.35)',
                    }}
                  >
                    <SparklesIcon className="w-7 h-7 text-white" />
                  </motion.div>
                  <h3 className="text-xl font-bold mb-2 text-gray-900">
                    Memory that{' '}
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-400">
                      actually sticks
                    </span>
                  </h3>
                  <p className="text-sm leading-relaxed text-gray-600">
                    Highlights, questions, and answers stay connected — so understanding doesn't break.
                  </p>
                </div>
              </TiltCard>
            </motion.div>

            {/* Card 2: Center brand showcase */}
            <motion.div variants={itemVariants}>
              <TiltCard
                disableTilt={isMobile}
                className="group relative overflow-hidden rounded-2xl border border-gray-400/70 min-h-[220px] cursor-pointer"
                style={{
                  background: 'linear-gradient(180deg, rgba(6, 182, 212, 0.08) 0%, rgba(59, 130, 246, 0.05) 50%, rgba(255, 255, 255, 0.9) 100%)',
                  backdropFilter: 'blur(20px)',
                }}
              >
                {/* Animated gradient background */}
                <motion.div
                  className="absolute inset-0 opacity-30"
                  animate={{
                    background: [
                      'radial-gradient(circle at 20% 20%, rgba(6, 182, 212, 0.3) 0%, transparent 50%)',
                      'radial-gradient(circle at 80% 80%, rgba(59, 130, 246, 0.3) 0%, transparent 50%)',
                      'radial-gradient(circle at 20% 80%, rgba(14, 116, 144, 0.28) 0%, transparent 50%)',
                      'radial-gradient(circle at 20% 20%, rgba(6, 182, 212, 0.3) 0%, transparent 50%)',
                    ],
                  }}
                  transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
                />
                <div className="absolute top-4 left-0 right-0 text-center">
                  <motion.div
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 border border-gray-200/50"
                    whileHover={{ scale: 1.05 }}
                  >
                    <Logo variant="app-icon" size={24} isDarkMode={false} />
                    <span className="font-medium text-xs text-gray-600">vidya.ai</span>
                  </motion.div>
                </div>
                <div className="absolute inset-0 flex items-center justify-center" style={{ transform: 'translateZ(40px)' }}>
                  <div className="text-center px-4">
                    <h3 className="text-2xl font-bold leading-tight mb-1 text-gray-900">
                      Context-first
                    </h3>
                    <motion.span
                      className="text-2xl font-bold bg-clip-text text-transparent"
                      style={{
                        backgroundImage: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
                      }}
                      animate={{ opacity: [0.8, 1, 0.8] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      learning
                    </motion.span>
                  </div>
                </div>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
                  <motion.div
                    animate={{
                      scale: [1, 1.3, 1],
                      opacity: [0.3, 0.6, 0.3],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                    className="w-20 h-20 rounded-full"
                    style={{
                      background: 'radial-gradient(circle, rgba(6, 182, 212, 0.6) 0%, rgba(59, 130, 246, 0.2) 40%, transparent 70%)',
                      filter: 'blur(15px)',
                    }}
                  />
                </div>
              </TiltCard>
            </motion.div>

            {/* Card 3: Instant Answers */}
            <motion.div variants={itemVariants}>
              <TiltCard
                disableTilt={isMobile}
                className={`group relative overflow-hidden rounded-2xl border p-6 h-full cursor-pointer border-gray-400/70`}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(20px)',
                }}
              >
                <motion.div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background: 'radial-gradient(circle at 70% 70%, rgba(6, 182, 212, 0.15) 0%, transparent 60%)',
                  }}
                />
                <div className="flex items-center justify-between relative z-10" style={{ transform: 'translateZ(30px)' }}>
                  <div>
                    <h3 className={`text-xl font-bold mb-1 text-gray-900`}>
                      <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-400">
                        Answers in context
                      </span>
                    </h3>
                    <span className={`text-sm text-gray-600`}>Ask while you read. Get grounded explanations.</span>
                  </div>
                  <motion.div
                    whileHover={{ rotate: 15, scale: 1.15 }}
                    className="w-14 h-14 rounded-2xl flex items-center justify-center"
                    style={{
                      background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
                      boxShadow: '0 8px 30px rgba(6, 182, 212, 0.25)',
                    }}
                  >
                    <BoltIcon className="w-7 h-7 text-white" />
                  </motion.div>
                </div>
              </TiltCard>
            </motion.div>
          </div>

          {/* Row 2: Book library + Live demo */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Card 4: Book previews */}
            <motion.div variants={itemVariants}>
              <TiltCard
                disableTilt={isMobile}
                className={`group relative overflow-hidden rounded-2xl border p-5 cursor-pointer border-gray-400/70 h-full`}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(20px)',
                }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <BookOpenIcon className="w-4 h-4 text-cyan-400" />
                    <span className={`text-sm font-medium text-gray-700`}>Your library</span>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full text-gray-500 bg-gray-100/50`}>OpenStax</span>
                </div>
                <div className="grid grid-cols-6 gap-2">
                  {books.map((book, i) => (
                    <motion.div
                      key={book.name}
                      onHoverStart={() => setHoveredBook(i)}
                      onHoverEnd={() => setHoveredBook(null)}
                      whileHover={{ scale: 1.15, y: -4, zIndex: 10 }}
                      className="aspect-[3/4] rounded-lg cursor-pointer overflow-hidden relative"
                      style={{
                        boxShadow: hoveredBook === i ? '0 8px 25px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
                      }}
                    >
                      <img
                        src={book.cover}
                        alt={book.name}
                        className="w-full h-full object-cover"
                      />
                      {hoveredBook === i && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="absolute inset-0 bg-black/10"
                        />
                      )}
                    </motion.div>
                  ))}
                </div>
                <motion.p
                  className={`text-xs mt-4 text-center h-4 text-gray-500`}
                  animate={{ opacity: hoveredBook !== null ? 1 : 0.6 }}
                >
                  {hoveredBook !== null ? (
                    <span className="text-cyan-400 font-medium">{books[hoveredBook].name}</span>
                  ) : (
                    'Hover to explore'
                  )}
                </motion.p>
              </TiltCard>
            </motion.div>

            {/* Card 5: Live demo */}
            <motion.div variants={itemVariants}>
              <TiltCard
                disableTilt={isMobile}
                className={`group relative overflow-hidden rounded-2xl border p-5 cursor-pointer border-gray-400/70 h-full min-h-[220px]`}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(20px)',
                }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <ChatBubbleBottomCenterTextIcon className="w-4 h-4 text-cyan-400" />
                    <span className={`text-sm font-medium text-gray-700`}>AI Chat</span>
                  </div>
                  <div className="flex items-center gap-1.5 bg-green-500/10 px-2 py-1 rounded-full border border-green-500/20">
                    <motion.div
                      animate={{ scale: [1, 1.3, 1], opacity: [0.7, 1, 0.7] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      className="w-2 h-2 rounded-full bg-green-500"
                    />
                    <span className="text-[10px] text-green-400 font-medium">Live</span>
                  </div>
                </div>
                <TypingDemo inView={isInView} />
              </TiltCard>
            </motion.div>
          </div>

          {/* Row 3: Feature pills + Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {/* Card 6: 24/7 feature */}
            <motion.div variants={itemVariants}>
              <motion.div
                whileHover={{ y: -6, scale: 1.02 }}
                className={`group relative overflow-hidden rounded-2xl border p-5 cursor-pointer h-full border-gray-400/70`}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(20px)',
                }}
              >
                <div className="flex items-center gap-3">
                  <motion.div
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                    className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{
                      background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.28) 0%, rgba(59, 130, 246, 0.24) 100%)',
                      border: '1px solid rgba(6, 182, 212, 0.45)',
                      boxShadow: '0 6px 18px rgba(6, 182, 212, 0.18)',
                    }}
                  >
                    <ClockIcon className="w-5 h-5 text-cyan-700" />
                  </motion.div>
                  <div>
                    <h4 className={`font-semibold text-sm text-gray-900`}>Always on</h4>
                    <p className={`text-xs text-gray-600`}>Help, whenever you need it</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Card 7: Context-aware */}
            <motion.div variants={itemVariants}>
              <motion.div
                whileHover={{ y: -6, scale: 1.02 }}
                className={`group relative overflow-hidden rounded-2xl border p-5 cursor-pointer h-full border-gray-400/70`}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(20px)',
                }}
              >
                <div className="flex items-center gap-3">
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{
                      background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.28) 0%, rgba(59, 130, 246, 0.24) 100%)',
                      border: '1px solid rgba(6, 182, 212, 0.45)',
                      boxShadow: '0 6px 18px rgba(6, 182, 212, 0.18)',
                    }}
                  >
                    <ChatBubbleBottomCenterTextIcon className="w-5 h-5 text-cyan-700" />
                  </motion.div>
                  <div>
                    <h4 className={`font-semibold text-sm text-gray-900`}>Knows your learning</h4>
                    <p className={`text-xs text-gray-600`}>Answers with context</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Card 8: 6 Subjects */}
            <motion.div variants={itemVariants} className="sm:col-span-2">
              <motion.div
                whileHover={{ y: -6, scale: 1.01 }}
                className={`group relative overflow-hidden rounded-2xl border p-5 cursor-pointer h-full border-gray-400/70`}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(20px)',
                }}
              >
                <div className="flex items-center justify-between h-full">
                  <div>
                    <h3
                      className="text-3xl font-bold bg-clip-text text-transparent"
                      style={{
                        backgroundImage: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
                      }}
                    >
                      6 Subjects
                    </h3>
                    <span className={`text-sm text-gray-600`}>More coming soon</span>
                  </div>
                  <div className="flex gap-2">
                    {books.map((book, i) => (
                      <motion.div
                        key={i}
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.1 * i }}
                        whileHover={{ scale: 1.2, y: -4 }}
                        className="w-10 h-10 rounded-lg overflow-hidden cursor-pointer"
                        style={{ boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}
                        title={book.name}
                      >
                        <img
                          src={book.cover}
                          alt={book.name}
                          className="w-full h-full object-cover"
                        />
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>

        </motion.div>
      </div>
    </section>
  );
};

export default Features;
